/**
 * useCmsHeroSlides — homepage hero slides, fresher than the bundle.
 *
 * Mirrors `useCmsLectures`: the build-time snapshot (`hero-slides.generated.ts`)
 * seeds state so prerendered HTML is complete, then `/api/cms/public/hero-slides`
 * replaces it on mount so an operator's slide edit shows without a rebuild.
 *
 * Hidden slides are included in the API response; the renderer filters on
 * `isVisible`. An empty list means "no slides in the CMS" → the component
 * keeps the snapshot seed (which may be the compiled-in legacy slides).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsHeroSlides, type HeroSlideEntry } from '@/content/cms/hero-slides.generated'
import { heroWireToEntries, type HeroSlidesWireResp } from '@/content/cms/heroWire'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'

export function useCmsHeroSlides(): HeroSlideEntry[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  // At prerender time the Node side already fetched fresh CMS data into the
  // context; use it directly so crawlers see fresh content without client JS.
  const initial = cmsData?.heroSlides ?? cmsHeroSlides
  const [slides, setSlides] = useState<HeroSlideEntry[]>(initial)

  useEffect(() => {
    // Prerender already supplied fresh CMS data; skip the runtime fetch.
    if (cmsData?.heroSlides) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/hero-slides?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<HeroSlidesWireResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.slides?.length) return
        // The wire payload is flat + snake_case; the snapshot shape is
        // camelCase with the copy nested per locale. Convert, or every slide
        // reads as `isVisible === undefined`, the visibility filter drops them
        // all, and the hero renders empty.
        setSlides(heroWireToEntries(j.slides, locale))
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.heroSlides])

  return slides
}
